import React, { useEffect, useRef, useState } from 'react'
import { CATEGORY_SELECT_ICON } from 'image/icon'
import { MapStore } from 'store/map'
import { observer } from 'mobx-react'
import {
  BottomButton,
  Container,
  IconButton,
  ShadowWrapper,
} from './bottom-button-common'
import { Animated, TouchableOpacity, View } from 'react-native'
import { categoryStore } from 'store/category'
import { categoryFilterStore } from 'store/category-filter'
import { colors } from 'infra/color'
import { ButtonShadow } from 'ui/home/bottom-button-styles'
import { Body2, H2 } from 'ui/common/text'
import {
  BOTTOM_VIEW_BOTTOM_DISTANCE,
  BOTTOM_VIEW_HORIZONTAL_DISTANCE,
} from 'infra/constant'

/** Gap from FAB bottom to category chip list bottom (48 FAB + 8 margin). */
const CATEGORY_LIST_BOTTOM_OFFSET = BOTTOM_VIEW_BOTTOM_DISTANCE + 56

const TouchableOpacityCommonStyle = {
  backgroundColor: colors.white,
  paddingHorizontal: 10,
  paddingVertical: 7,
  shadowColor: '#000',
  shadowOffset: { width: 2, height: 3 },
  shadowOpacity: 0.25,
  shadowRadius: 6,
  elevation: 6,
  borderWidth: 1,
  borderRadius: 100,
  borderColor: colors.white,
}

export const CategorySelectButton: React.FC<{
  mapStore: MapStore
}> = observer(({ mapStore }) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectedCategory = categoryFilterStore.selectedCategory

  // Extract emoji from category name (emoji is at the beginning)
  const getEmoji = (name: string) => {
    // Handle compound emojis like 🧑‍💻 by matching emoji sequences
    const match = name.match(
      /^([\p{Emoji}\p{Emoji_Modifier}\p{Emoji_Component}\u200d]+)/u,
    )
    return match ? match[1] : null
  }

  // Create animation values for each category + 전체 칩
  const categoryAnims = useRef<any[]>([])

  // Initialize animation values when categories change
  const categoryDataLength = categoryStore.raw.length
  useEffect(() => {
    categoryAnims.current = [...categoryStore.raw, { id: 'all' }].map(() => ({
      opacity: new Animated.Value(0),
      translateY: new Animated.Value(30),
      scale: new Animated.Value(0.8),
    }))
  }, [categoryDataLength, categoryAnims])

  const toggleCategories = () => {
    if (isOpen) {
      // Close animation - from top to bottom
      const animations = categoryAnims.current.map((anim, index) => {
        return Animated.parallel([
          Animated.timing(anim.opacity, {
            toValue: 0,
            duration: 90,
            delay: index * 15,
            useNativeDriver: true,
          }),
          Animated.timing(anim.translateY, {
            toValue: 30,
            duration: 90,
            delay: index * 15,
            useNativeDriver: true,
          }),
          Animated.timing(anim.scale, {
            toValue: 0.8,
            duration: 90,
            delay: index * 15,
            useNativeDriver: true,
          }),
        ])
      })
      Animated.parallel(animations).start(() => setIsOpen(false))
    } else {
      // Open animation - staggered from bottom to top
      setIsOpen(true)
      const animations = categoryAnims.current.map((anim, index) => {
        const reverseIndex = categoryAnims.current.length - 1 - index
        return Animated.parallel([
          Animated.spring(anim.opacity, {
            toValue: 1,
            delay: reverseIndex * 12,
            useNativeDriver: true,
          }),
          Animated.spring(anim.translateY, {
            toValue: 0,
            delay: reverseIndex * 12,
            tension: 160,
            friction: 7,
            useNativeDriver: true,
          }),
          Animated.spring(anim.scale, {
            toValue: 1,
            delay: reverseIndex * 12,
            tension: 160,
            friction: 7,
            useNativeDriver: true,
          }),
        ])
      })
      Animated.parallel(animations).start()
    }
  }

  const handleCategorySelect = (category: any) => {
    // Toggle selection if same category is clicked
    if (selectedCategory?.id === category.id) {
      categoryFilterStore.clearSelection()
    } else {
      categoryFilterStore.selectCategory(category)
    }
    toggleCategories()
  }

  if (mapStore.selectedPlace) return null

  return (
    <>
      {isOpen && (
        <>
          {/* Backdrop */}
          <TouchableOpacity
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'transparent',
            }}
            onPress={toggleCategories}
            activeOpacity={1}
          />

          {/* Category List */}
          <View
            style={{
              position: 'absolute',
              left: BOTTOM_VIEW_HORIZONTAL_DISTANCE,
              bottom: CATEGORY_LIST_BOTTOM_OFFSET,
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {categoryStore.raw.map((category, index) => (
              <Animated.View
                key={category.id}
                style={{
                  marginBottom: 8,
                  opacity: categoryAnims.current[index]?.opacity || 0,
                  transform: [
                    {
                      translateY: categoryAnims.current[index]?.translateY || 0,
                    },
                    { scale: categoryAnims.current[index]?.scale || 0.8 },
                  ],
                }}
              >
                <TouchableOpacity
                  onPress={() => handleCategorySelect(category)}
                  style={{
                    ...TouchableOpacityCommonStyle,
                    borderColor:
                      selectedCategory?.id === category.id
                        ? colors.primary
                        : colors.white,
                  }}
                  activeOpacity={0.8}
                >
                  <Body2
                    style={{
                      fontWeight: '700',
                      color:
                        selectedCategory?.id === category.id
                          ? colors.primary
                          : colors.black,
                    }}
                  >
                    {category.name}
                    <Body2 style={{ fontWeight: '400' }}>
                      {' '}
                      ({category.place_count})
                    </Body2>
                  </Body2>
                </TouchableOpacity>
              </Animated.View>
            ))}

            {/* 선택 해제 칩 */}
            <Animated.View
              style={{
                opacity:
                  categoryAnims.current[categoryStore.raw.length]?.opacity || 0,
                transform: [
                  {
                    translateY:
                      categoryAnims.current[categoryStore.raw.length]
                        ?.translateY || 0,
                  },
                  {
                    scale:
                      categoryAnims.current[categoryStore.raw.length]?.scale ||
                      0.8,
                  },
                ],
              }}
            >
              <TouchableOpacity
                onPress={() => {
                  categoryFilterStore.clearSelection()
                  toggleCategories()
                }}
                style={TouchableOpacityCommonStyle}
                activeOpacity={0.8}
              >
                <Body2
                  style={{
                    fontWeight: '700',
                    color: colors.gray.v600,
                  }}
                >
                  선택 해제
                </Body2>
              </TouchableOpacity>
            </Animated.View>
          </View>
        </>
      )}

      {selectedCategory && getEmoji(selectedCategory.name) ? (
        <Container
          style={[
            {
              bottom: BOTTOM_VIEW_BOTTOM_DISTANCE,
              left: BOTTOM_VIEW_HORIZONTAL_DISTANCE,
            },
          ]}
          pointerEvents='box-none'
        >
          <IconButton onPress={toggleCategories} activeOpacity={0.8}>
            <ShadowWrapper style={ButtonShadow}>
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 18,
                  backgroundColor: colors.white,
                  borderWidth: 2,
                  borderColor: colors.primary,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <H2>{getEmoji(selectedCategory.name)}</H2>
              </View>
            </ShadowWrapper>
          </IconButton>
        </Container>
      ) : (
        <BottomButton
          onPress={toggleCategories}
          imageSource={CATEGORY_SELECT_ICON}
          style={{ left: BOTTOM_VIEW_HORIZONTAL_DISTANCE }}
        />
      )}
    </>
  )
})
